import Food from "@/components/blocks/theme/eco-effect/food"
import Button from "@/components/common/button/button"
import { usePropsObject } from "@/lib/contexts/appContext"
import cx from "classnames"
import { motion } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import { A11y, Navigation } from "swiper/modules"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import Chevron from "@/common/icons/chevron"
import { Share as ShareIcon } from "@/common/icons/share"
import Block from "@/components/wrappers/block"
import { Counter } from "./counter"
import { foodData, peopleData } from "./data"
import { Dialog } from "./dialog"
import Input from "./input"
import Result from "./result"
import { swiperAlt } from "./swiper-alt.module.css"
import { swiper } from "./swiper.module.css"

export default function EcoEffect() {
  const { share } = usePropsObject()
  const [food, setFood] = useState(share?.food || [])
  const [people, setPeople] = useState(share?.people || 1)
  const [customPeople, setCustomPeople] = useState("")
  const modalRef = useRef(null)
  const prevFoodRef = useRef(null)
  const nextFoodRef = useRef(null)
  const prevPeopleRef = useRef(null)
  const nextPeopleRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [tracked, setTracked] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  const dataLayerPush = (campaignAction = "product") => {
    if (tracked) {
      return
    }

    if (typeof window.dataLayer === "undefined") {
      return
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: "campaign-interaction",
      campaignAction
    })
    setTracked(true)
  }

  const submit = (e) => {
    e.preventDefault()
    modalRef.current.showModal()
    setIsOpen(true)
  }

  const onSwiperResize = (swiper) => {
    swiper.slideTo(0)

    if (window.innerWidth > 1024) {
      swiper.enabled = false
    } else {
      swiper.enabled = true
    }
  }

  const swiperBreakpoints = {
    640: {
      spaceBetween: 8
    },
    768: {
      spaceBetween: 12
    },
    1024: {
      enabled: false
    }
  }

  return (
    <Block className="overflow-x-hidden">
      {mounted && (
        <form onSubmit={submit}>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 100,
              duration: 0.5,
              delay: 0.5
            }}>
            <h2 className="mb-8 font-normal h5">
              Byt en eller flera matvaror till ekologiskt för att se hur mycket
              naturfrämmande bekämpningsmedel och konstgödsel du stoppar på ett
              år. Och testa gärna vad som händer när fler byter till eko.
            </h2>
          </motion.div>
          <Result people={people} food={food} />
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 100,
              duration: 0.5,
              delay: 2
            }}>
            <div className="grid grid-cols-1 gap-4 mb-12 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-8">
                <div className="flex justify-between select-none lg:justify-start">
                  <h3 className="mr-4 h5">Byt till ekologiskt</h3>
                  <span className="block font-serif text-base px-3 py-1 bg-summer-green-50 rounded-[32px]">
                    {food.length} av {Object.keys(foodData).length}
                  </span>
                </div>

                <div className="py-4">
                  <Swiper
                    className={`${swiper} ${swiperAlt}`}
                    modules={[Navigation, A11y]}
                    spaceBetween={8}
                    slidesPerView="auto"
                    slideToClickedSlide="true"
                    breakpoints={swiperBreakpoints}
                    navigation={{
                      nextEl: ".swiper-nav-next",
                      prevEl: ".swiper-nav-prev"
                    }}
                    onBeforeInit={(swiper) => {
                      swiper.params.navigation.prevEl = prevFoodRef.current
                      swiper.params.navigation.nextEl = nextFoodRef.current
                    }}
                    onResize={onSwiperResize}>
                    {foodData.map(({ label, icon }, index) => (
                      <SwiperSlide key={index}>
                        <Food
                          label={label}
                          icon={icon}
                          checked={food.includes(label)}
                          index={index}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFood([...food, e.target.value])
                            } else {
                              setFood(food.filter((v) => v !== e.target.value))
                            }

                            dataLayerPush()
                          }}
                        />
                      </SwiperSlide>
                    ))}
                    <div className="no-print lg:hidden">
                      <button
                        type="button"
                        aria-label="Föregående"
                        className="swiper-nav-prev swiper-nav"
                        ref={prevFoodRef}>
                        <Chevron className="w-full h-auto transform rotate-90 fill-current" />
                      </button>
                      <button
                        type="button"
                        aria-label="Nästa"
                        className="swiper-nav-next swiper-nav"
                        ref={nextFoodRef}>
                        <Chevron className="w-full h-auto transform -rotate-90 fill-current" />
                      </button>
                    </div>
                  </Swiper>
                </div>
              </div>

              <div className="lg:col-span-4">
                <div className="flex justify-between mb-4 select-none lg:justify-start">
                  <h3 className="mr-4 h5">Antal personer</h3>
                  <span className="block font-serif text-base px-3 py-1 bg-summer-green-50 rounded-[32px]">
                    <Counter value={people} /> st
                  </span>
                </div>
                <Swiper
                  className={`${swiper} ${swiperAlt}`}
                  modules={[Navigation, A11y]}
                  spaceBetween={8}
                  slidesPerView="auto"
                  slideToClickedSlide="true"
                  breakpoints={swiperBreakpoints}
                  navigation={{
                    nextEl: ".swiper-nav-next",
                    prevEl: ".swiper-nav-prev"
                  }}
                  onBeforeInit={(swiper) => {
                    swiper.params.navigation.prevEl = prevPeopleRef.current
                    swiper.params.navigation.nextEl = nextPeopleRef.current
                  }}
                  onResize={onSwiperResize}>
                  {peopleData.map((value) => (
                    <SwiperSlide key={value.label}>
                      <Input
                        label={value.label}
                        value={value.value}
                        checked={people === value.value && !customPeople}
                        onChange={(e) => {
                          setPeople(parseInt(e.target.value))
                          setCustomPeople("")
                          dataLayerPush("People")
                        }}
                      />
                    </SwiperSlide>
                  ))}
                  <SwiperSlide key="custom-input">
                    <input
                      value={customPeople}
                      type="number"
                      className={cx(
                        "bg-white h-14 w-36 rounded-[64px] px-4 cursor-pointer border-2 border-summer-green-50 transition-colors duration-200 hover:border-summer-green-100 placeholder:text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                        {
                          "border-summer-green-100": customPeople > 0
                        }
                      )}
                      placeholder="Valfritt antal"
                      onChange={(e) => {
                        const value = e.target.value
                        setCustomPeople(value)

                        if (value > 0) {
                          setPeople(parseInt(value))
                          dataLayerPush("People")
                        }
                      }}
                    />
                  </SwiperSlide>
                  <div className="no-print lg:hidden">
                    <button
                      type="button"
                      aria-label="Föregående"
                      className="swiper-nav-prev swiper-nav"
                      ref={prevPeopleRef}>
                      <Chevron className="w-full h-auto transform rotate-90 fill-current" />
                    </button>
                    <button
                      type="button"
                      aria-label="Nästa"
                      className="swiper-nav-next swiper-nav"
                      ref={nextPeopleRef}>
                      <Chevron className="w-full h-auto transform -rotate-90 fill-current" />
                    </button>
                  </div>
                </Swiper>
              </div>
            </div>
          </motion.div>
          {people > 0 && food.length > 0 && (
            <>
              <Button
                theme="lime"
                ariaLabel="Dela din ekoeffekt"
                className="flex items-center justify-center w-full button sm:w-auto sm:mx-auto"
                type="submit">
                Dela din ekoeffekt!
                <ShareIcon className="h-5 ml-3" />
              </Button>
              <Dialog
                people={people}
                food={food}
                modalRef={modalRef}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
              />
            </>
          )}
        </form>
      )}
    </Block>
  )
}
